// components/season/EditSeason.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import flatpickr from 'flatpickr';

function EditSeason() {
  const { seasonId } = useParams();
  const navigate = useNavigate();

  // Text fields in local state
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
  });

  // If a banner already exists, store it
  const [currentBanner, setCurrentBanner] = useState(null);

  // Refs for file input and flatpickr inputs
  const fileInputRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  // Fetch existing season data once on mount / seasonId change
  useEffect(() => {
    axios
      .get(`/api/leagues/seasons/${seasonId}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      })
      .then((response) => {
        setFormData({
          name: response.data.name || '',
          start_date: response.data.start_date || '',
          end_date: response.data.end_date || '',
        });
        if (response.data.banner) {
          setCurrentBanner(response.data.banner);
        }
      })
      .catch((error) => {
        console.error('Error fetching season data:', error);
        alert('Unable to fetch season details.');
      });
  }, [seasonId]);

  // Initialize Flatpickr on our start_date input after component render
  useEffect(() => {
    // Start Date Flatpickr
    if (startDateRef.current) {
      flatpickr(startDateRef.current, {
        dateFormat: 'Y-m-d',
        defaultDate: formData.start_date, // show existing date if present
        onChange: (selectedDates, dateStr) => {
          setFormData((prev) => ({ ...prev, start_date: dateStr }));
        },
      });
    }

    // End Date Flatpickr
    if (endDateRef.current) {
      flatpickr(endDateRef.current, {
        dateFormat: 'Y-m-d',
        defaultDate: formData.end_date, // show existing date if present
        onChange: (selectedDates, dateStr) => {
          setFormData((prev) => ({ ...prev, end_date: dateStr }));
        },
      });
    }
    // We only want to initialize flatpickr once, or whenever formData.*_date changes if you prefer
  }, [formData.start_date, formData.end_date]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('start_date', formData.start_date);
    formDataToSend.append('end_date', formData.end_date);

    if (fileInputRef.current && fileInputRef.current.files[0]) {
      formDataToSend.append('banner', fileInputRef.current.files[0]);
    }

    axios
      .put(`/api/leagues/seasons/${seasonId}/`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      })
      .then(() => {
        navigate('/profile');
      })
      .catch((error) => {
        console.error('Error updating season:', error);
        alert('Failed to update the season. Please try again.');
      });
  };

  return (
    <div className="container">
      <div className="card p-4">
        <h5>Edit Season</h5>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Season Name</label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          {/* Start Date Field (Flatpickr) */}
          <div className="mb-3">
            <label className="form-label">Start Date</label>
            <input
              type="text"
              className="form-control flatpickr-input"
              name="start_date"
              ref={startDateRef} // Attach ref for Flatpickr
              value={formData.start_date} // Keep controlled approach
              onChange={handleChange}      // For fallback, though onChange is mostly overridden by Flatpickr
              required
            />
          </div>
          {/* End Date with Flatpickr */}
          <div className="mb-3">
            <label className="form-label">End Date</label>
            <input
              type="text"
              className="form-control flatpickr-input"
              name="end_date"
              ref={endDateRef} // attach to ref for flatpickr init
              defaultValue={formData.end_date}
              onChange={handleChange}
              required
            />
          </div>
          {currentBanner && (
            <div className="mb-3">
              <p>Current Banner:</p>
              <img
                src={currentBanner}
                alt="Current Banner"
                style={{ maxWidth: '300px', height: 'auto', marginBottom: '10px' }}
              />
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">Banner Image</label>
            <input
              type="file"
              className="form-control"
              ref={fileInputRef}
              accept="image/*"
            />
          </div>
          <div className="d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/profile')}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditSeason;
